/**
 * Permission Dependencies Configuration
 *
 * This module provides utility functions for handling permission dependencies.
 * The actual dependency data comes from the database via tRPC endpoint.
 *
 * Key concepts:
 * - dependentPermission: A permission that requires another permission (e.g., "edit")
 * - requiredPermission: A permission that is required by another (e.g., "view")
 *
 * When a dependent permission is selected:
 * - All its required permissions are automatically selected and disabled
 *
 * When a required permission is deselected:
 * - All permissions that depend on it are automatically deselected
 */

/**
 * The dependency map type: dependentCode -> requiredCodes[]
 */
export type PermissionDependencyMap = Record<string, string[]>;

/**
 * Get all permissions that depend on a given permission
 * (reverse lookup - find all permissions that have this one as a dependency)
 */
export function getDependentPermissions(permissionCode: string, dependencyMap: PermissionDependencyMap): string[] {
  return Object.entries(dependencyMap)
    .filter(([_, dependencies]) => dependencies.includes(permissionCode))
    .map(([dependent]) => dependent);
}

/**
 * Get all permissions that a given permission requires
 */
export function getRequiredPermissions(permissionCode: string, dependencyMap: PermissionDependencyMap): string[] {
  return dependencyMap[permissionCode] ?? [];
}

/**
 * Get all permissions recursively required by a permission
 * (handles nested dependencies)
 */
export function getAllRequiredPermissions(
  permissionCode: string,
  dependencyMap: PermissionDependencyMap,
  visited = new Set<string>()
): string[] {
  if (visited.has(permissionCode)) return [];
  visited.add(permissionCode);

  const directDependencies = getRequiredPermissions(permissionCode, dependencyMap);
  const allDependencies = [...directDependencies];

  for (const dep of directDependencies) {
    allDependencies.push(...getAllRequiredPermissions(dep, dependencyMap, visited));
  }

  return Array.from(new Set(allDependencies));
}

/**
 * Get all permissions that recursively depend on a permission
 * (handles nested dependents)
 */
export function getAllDependentPermissions(
  permissionCode: string,
  dependencyMap: PermissionDependencyMap,
  visited = new Set<string>()
): string[] {
  if (visited.has(permissionCode)) return [];
  visited.add(permissionCode);

  const directDependents = getDependentPermissions(permissionCode, dependencyMap);
  const allDependents = [...directDependents];

  for (const dep of directDependents) {
    allDependents.push(...getAllDependentPermissions(dep, dependencyMap, visited));
  }

  return Array.from(new Set(allDependents));
}

interface Permission {
  id: string;
  code: string;
  name: string;
}

/**
 * Generate a user-friendly message explaining why a permission is required
 */
export function getRequiredByMessage(
  _permissionCode: string,
  dependentCodes: string[],
  allPermissions: Permission[]
): string {
  const dependentNames = dependentCodes
    .map((code) => {
      const permission = allPermissions.find((p) => p.code === code);
      return permission?.name || code;
    })
    .slice(0, 3);

  const remaining = dependentCodes.length - 3;

  if (remaining > 0) {
    return `Obrigatória para: ${dependentNames.join(', ')} e mais ${remaining}`;
  }

  return `Obrigatória para: ${dependentNames.join(', ')}`;
}
